//TODO
//  -   Check input before submit
//  -   convert comma separated into boxes with a X to add/remove names or roles
//

import { Check, Copy, ExternalLink, Info, Pencil, Trash2, X } from "lucide-react";
import Button from "./components/button/Button";
import { useEffect, useMemo, useState } from "react";
import EntitySelector from "./components/forms/EntitySelector";
import type { Constraint, Data, Definition, Employee, EntityRef, Role, RoleUsersGrid } from "./types"
import { getRoleOrders, sortRoles } from "@/utils/roleUtils";
import {
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectLabel,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select"
import { Textarea } from "./components/ui/textarea";
import { BASE_URL } from "@/global";
import NumberInput from "./components/forms/NumberInput";
import SearchInput from "@/components/forms/SearchInput";
import { normalize } from "@/lib/utils";
import { type SearchableOption, SearchableSelect } from "@/components/forms/SearchableSelect";
import { Switch } from "@/components/forms/Switch";
import ConfirmModal from "@/components/modals/ConfirmModal";
import { Tooltip } from "@/components/tooltips/Tooltip";
import RoleCombinations from "@/tabs/RoleCombinations/RoleCombinations";
import SegmentedTabs from "@/components/SegmentedTabs";
import CollapsibleSection from "@/components/sections/CollapsibleSection";
import CheckboxField from "@/components/forms/CheckboxField";
import RoleUsersGridModal from "@/components/modals/RoleUsersGridModal";

//ALWAYS HAS TO BE at least WORDS FOR LABEL IN "TEXT" (not for select) I KNOW ITS FUCKED UP (Yea we need to change that, I just got caught again)
const translation: Record<string, string> = {
    time: "une plage horaire",
    totalDays: "La Quantité de jours",
    role: "des rôles",
    user: "des utilisateurs",
    user_match: "des utilisateurs requis",
    event: "un type de périodes",
    day: "un jours de la semaine",
    day_ext: "un jours de la semaine",
    amount: "une quantité",
    hard: "obligatoire",
    soft: "facultatif",
    weight: "un poids",
    priority_type: "l' Importance de la contrainte",
    type: "un type",
    role_to: "des rôles exclus",
    amount_shift: "une quantité de quarts",
    amount_time: "une quantité de périodes",
    amount_role: "une quantité de rôles",
    time_before: "une quantité de périodes avant",
    time_after: "une quantité de périodes après",
    ratio: "un ratio de charge de travail",
    total: "une quantité total de quarts de travail",
    total_days: "une quantité total de jours",
    role_match: "les rôles requis",
    role_ex: "le rôles non exclus",
    day_match: "Les jours de semaine pour rôles requis",
    day_to: "Les jours de semaine pour rôles exclus",
    note: "Les notes",
    role_with: "des rôles simultanée",
    day_with: "un jours de la semaine associé au rôles simultanée",
    name: "un nom de contrainte",
    option: "les options",
    score: "les scores",
    period: "les périodes",
    exception: "les exceptions",
    grid: "une grille de sélection",
}

const attributeDefinitions: Record<string, string> = {
    time: "Définit une plage horaire spécifique durant laquelle la contrainte s'applique",
    totalDays: "La Quantité de jours",
    time_before: "Nombre de périodes qui doivent précéder un événement",
    time_after: "Nombre de périodes qui doivent suivre un événement",
    role: "Les rôles concernés par cette contrainte dans votre organisation",
    role_to: "Les rôles qui ne peuvent pas être combinés avec les rôles spécifiés",
    user: "Les utilisateurs spécifiques auxquels s'applique cette contrainte",
    user_match: "Le utilisateur spécifique auquel s'applique cette contrainte",
    event: "Le type de période ou d'événement concerné par la contrainte",
    day: "Les jours de la semaine auquel s'applique cette contrainte",
    day_ext: "Les jours de la semaine auquel s'applique cette contrainte",
    amount: "La quantité ou le nombre d'occurrences autorisées",
    amount_shift: "Le nombre de quarts de travail concernés",
    amount_time: "Le nombre de périodes de temps concernées",
    amount_role: "Le nombre de rôles différents concernés",
    weight: "L'importance relative de cette contrainte (poids dans le calcul)",
    priority_type: "Niveau de l'importance de cette contrainte",
    type: "Indique si cette contrainte est obligatoire (hard) ou facultative (soft)",
    ratio: "un ratio de charge de travail",
    total: "une quantité total de quarts de travail",
    total_days: "une quantité total de jours",
    role_match: "Les rôles requis",
    role_ex: "Les rôles requis",
    day_match: "Les jours de semaine pour rôles requis",
    day_to: "Les jours de semaine pour rôles exclus",
    note: "Les notes",
    role_with: "Les rôles qui doivent être combinés avec les rôles spécifiés",
    day_with: "Les jours de la semaine auquel s'applique cette contrainte",
    name: "Le nom de la contrainte",
    option: "option",
    score: "score",
    period: "période",
    exception: "exception férié",
    grid: "Les rôles, jours et utilisateurs pris en compte, choisis rôle par rôle",
}

const constraintDescriptions: Record<string, string> = {
    "simulRoles": "2. GÉNÉRALE - Aucune combinaison simultanée possible pour le groupe de rôles pour un ou plusieurs utilisateurs",
    "simulRolesGroups": "2. GÉNÉRALE - Aucune combinaison simultanée possible pour deux groupes de rôles pour un ou plusieurs utilisateurs",
    "maxRoles": "2. GÉNÉRALE - Quantité de quarts maximale par plage horaire pour un ou plusieurs rôles et utilisateurs",
    "maxDays": "2. GÉNÉRALE - Quantité de jour maximale par plage horaire pour un ou plusieurs rôles et utilisateurs",
    "maxRolesRow": "5. ENCHAÎNEMENT ET ROTATION - Quantité de quarts maximale (DE SUITE) par plage horaire pour un ou plusieurs rôles et utilisateurs",
    "minRoles": "2. GÉNÉRALE - Quantité de quarts minimum pour une semaine",
    "minRolesShift": "2. GÉNÉRALE - Quantité de quarts comblés minimum pour une plage horaire",
    "spanBuffer": "2. GÉNÉRALE - Quantité de quarts maximum avant ou après un quart pour une quantité de temps",
    "mandatory": "1. BASE - Assigner les bris de service (uniquement)",
    "requiredSkills": "1. BASE - La personne assignée doit être compétente pour ce rôle",
    "minUnassigned": "1. BASE - Assigner les bris de services et quarts à combler",
    "equityWeekend": "3. ÉQUITÉ ET BALANCEMENT - Équité de fin de semaine",
    "equityHoliday": "3. ÉQUITÉ ET BALANCEMENT - Équité de férié",
    "equityNight": "4. RÉSERVÉES PLANIFICO (ne pas utiliser) -  Équité de nuit",
    "equityTotalShifts": "3. ÉQUITÉ ET BALANCEMENT - Équité des quarts de travail",
    "unavailable": "1. BASE - Ne pas assigner les personnes lors de leurs congés",
    "undesired": "1. BASE - Ne pas assigner les personnes lors de leurs indisponibles",
    "desired": "1. BASE - Tenter d'assigner les personnes lors de leurs préférence de disponibilités",
    "unassignedWeekend": "4. RÉSERVÉES PLANIFICO (ne pas utiliser) - Bris de service de weekend",
    "unassigned": "1. BASE - Assigner les quarts à combler pour certains rôles",
    "equityWeek": "3. ÉQUITÉ ET BALANCEMENT - Équité de semaine",
    "equityMonth": "3. ÉQUITÉ ET BALANCEMENT - Équité de mois",
    "equityEmployeeRoleDay": "3. ÉQUITÉ ET BALANCEMENT - Balancement des quarts selon la quantité maximale désirée de la table d'équité",
    "generalEquity-soft": "TEST",
    "equityAvailabilityRatio": "3. ÉQUITÉ ET BALANCEMENT - Équité selon jour disponible",
    "preferredRatio": "4. RÉSERVÉES PLANIFICO (ne pas utiliser) - Assigner les quarts aux utilisateurs selon un ratio (0-100)",
    "preferredShiftAmount": "4. RÉSERVÉES PLANIFICO (ne pas utiliser) - Quantité de quarts voulue pour la période",
    "preferredAmount": "4. RÉSERVÉES PLANIFICO (ne pas utiliser) - Assigner une quantité voulue de quarts pour un groupe de rôles",
    "preferredShifts": "2. GÉNÉRALE - Tenter d'assigner les personnes à leur rôles préférés",
    "beforeUnavailable": "2. GÉNÉRALE - Pas de quart d'un rôle avant les congés",
    "equityRole": "3. ÉQUITÉ ET BALANCEMENT -  Équité pour un groupe de rôles",
    "equityRoleGroupPerson": "3. ÉQUITÉ ET BALANCEMENT - Équité de groupes de rôle par personne",
    "equityEmployeeRole": "3. ÉQUITÉ ET BALANCEMENT - Balancement des quarts selon l'objectif de ratio en pourcentage de la table d'équité",
    "ifxtheny": "2. GÉNÉRALE - Doit faire les 'quarts requis' si assigné au quart spécifié",
    "ifxthenroley": "2. GÉNÉRALE - Assigne le quart requis si (et seulement si) l'utilisateur fait le quart spécifié",
    "ifXYthenPQ": "4. RÉSERVÉES PLANIFICO (ne pas utiliser) - Personne doit faire le 'quart requis' si personne désignée fait le quart spécifié",
    "equityEvenForEmployeesOnDayX": "3. ÉQUITÉ ET BALANCEMENT - Équité entre les employés pour un même jour",
    "equityAllRoles": "4. RÉSERVÉES PLANIFICO (ne pas utiliser) -  Équité pour tous les rôles",
    "spreadRolesOverTime": "3. ÉQUITÉ ET BALANCEMENT -  Répartir les tâches sur l'entièreté de la période",
    "allowedSimulRoles": "2. GÉNÉRALE - Aucun rôle en simultané sauf la paire spécifiée",
    "mixBlock": "2. GÉNÉRALE - Assigner un utilisateur à un groupe de roles durant un bloc de temps",
    "maxRolesWeekend": "2. GÉNÉRALE - Quantité maximum de fin de semaine",
    "simulRolesWithExclusiveRoleGroups": "2. GÉNÉRALE - Aucune combinaison simultanée possible pour deux groupes de rôles pour un ou plusieurs utilisateurs",
    "matchSimulRoles": "2. GÉNÉRALE - Forcer une combinaison simultanée (même jour) entre deux quarts pour des jours spécifiques",
    "matchSimulRolesPref": "2. GÉNÉRALE - Tente une combinaison simultanée (même jour) entre deux quarts pour des jours spécifiques",
    "sameWeekendEmployee": "2. GÉNÉRALE - Deux employés doivent faire les mêmes fins de semaine",
    "equity": "3. ÉQUITÉ ET BALANCEMENT - Équité sur la quantité totale de quarts de travail",
    "equityEmployeeDay": "3. ÉQUITÉ ET BALANCEMENT - Équité entre les employés pour un même jour",
    "availabilityBalance": "3. ÉQUITÉ ET BALANCEMENT - Équité selon jour disponible"
}

const CATEGORY_ORDER = ["BASE", "GENERAL", "EQUITY", "RESERVED", "SEQUENCE", "COMBINATIONS", "OTHER"] as const;

type CategoryKey = typeof CATEGORY_ORDER[number];

const KNOWN_CATEGORIES: Set<string> = new Set(CATEGORY_ORDER);

const CATEGORY_LABELS: Record<CategoryKey, string> = {
    BASE: "1. BASE",
    GENERAL: "2. GÉNÉRALE",
    EQUITY: "3. ÉQUITÉ ET BALANCEMENT",
    RESERVED: "4. RÉSERVÉES PLANIFICO (ne pas utiliser)",
    SEQUENCE: "5. ENCHAÎNEMENT ET ROTATION",
    COMBINATIONS: "6. COMBINAISONS DE TÂCHES",
    OTHER: "7. AUTRES",
};

const categoryLabel = (key: string): string =>
    (CATEGORY_LABELS as Record<string, string>)[key] ?? key;


const capitalize = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);

const DAYS_DATA = [
    { id: '0', name: 'Dernier Dimanche' },
    { id: '1', name: 'Lundi' },
    { id: '2', name: 'Mardi' },
    { id: '3', name: 'Mercredi' },
    { id: '4', name: 'Jeudi' },
    { id: '5', name: 'Vendredi' },
    { id: '6', name: 'Samedi' },
    { id: '7', name: 'Dimanche' },
    { id: '8', name: 'Prochain Lundi' },
];


const PRIORITIES_DATA = [
    { id: 'soft', name: 'Facultatif' },
    { id: 'hard', name: 'Obligatoire' },
];

function DataText({ placeholder, onChange, index, type, error }: any) {
    const [content, setContent] = useState(placeholder)
    const [showTooltip, setShowTooltip] = useState(false)

    useEffect(() => {
        type = type.split("_")[0]
    }, [])

    useEffect(() => {
        if (placeholder) {
            const displayValue = Array.isArray(placeholder) ? placeholder.join(",") : placeholder;
            setContent(displayValue);
        }
    }, [placeholder])

    const label = capitalize((translation[type] || "").split(" ").slice(1).join(" "));

    return (
        <div className="flex flex-col gap-1 w-full">
            <div className="relative w-fit">
                <label
                    className="text-sm font-medium"
                    onMouseEnter={() => setShowTooltip(true)}
                    onMouseLeave={() => setShowTooltip(false)}
                >
                    {label}
                </label>
                {showTooltip && attributeDefinitions[type] && (
                    <div
                        className="absolute z-[1000] bg-black/85 text-white rounded px-3 py-2 text-sm leading-relaxed pointer-events-none whitespace-normal max-w-xs left-0 bottom-full mb-1 w-max">
                        {attributeDefinitions[type]}
                    </div>
                )}
            </div>
            <Textarea className={`w-full max-h-[200px] bg-white ${error ? "border-red-500" : ""}`} placeholder={label}
                      value={content} onChange={e => {
                onChange(e, index)
                setContent(e.target.value)
            }}/>
            {error && <span className="text-red-600 text-xs mt-1">{error}</span>}
        </div>
    )
}


function DataSelector({ data, onSelect, index, type, placeholder: initialValue }: any) {
    const label = capitalize((translation[type] || "").split(" ").slice(1).join(" "));
    const placeholder = `Choisissez ${translation[type]}`;
    const [showTooltip, setShowTooltip] = useState(false);
    const [selectedValue, setSelectedValue] = useState<string>("");

    useEffect(() => {
        if (initialValue) {
            const displayValue = Array.isArray(initialValue) ? initialValue[0] : initialValue;
            setSelectedValue(displayValue);
        }
    }, [initialValue])

    return (
        <div className="flex flex-col gap-1 w-full">
            <div className="relative w-fit">
                <label
                    className="text-sm font-medium"
                    onMouseEnter={() => setShowTooltip(true)}
                    onMouseLeave={() => setShowTooltip(false)}
                >
                    {label}
                </label>
                {showTooltip && attributeDefinitions[type] && (
                    <div
                        className="absolute z-[1000] bg-black/85 text-white rounded px-3 py-2 text-sm leading-relaxed pointer-events-none whitespace-normal max-w-xs left-0 bottom-full mb-1 w-max">
                        {attributeDefinitions[type]}
                    </div>
                )}
            </div>
            <Select value={selectedValue} onValueChange={(e) => {
                setSelectedValue(e);
                onSelect([e], index);
            }}>
                <SelectTrigger className="w-full bg-white">
                    <SelectValue placeholder={capitalize(placeholder)}/>
                </SelectTrigger>
                <SelectContent>
                    <SelectGroup>
                        <SelectLabel>{label}</SelectLabel>
                        {
                            data.map((e: any, i: any) => {
                                return (
                                    <SelectItem key={i} value={e}>{e}</SelectItem>
                                )
                            })
                        }
                    </SelectGroup>
                </SelectContent>
            </Select>
        </div>
    )
}

type EntityConfig = {
    dataKey: 'users' | 'roles' | 'days' | 'priorities';
    fallbackKey: 'user' | 'role' | 'day' | 'priority';
    allLabel: string;
    keyPrefix: string;
    valueKey?: 'id' | 'name';
    multiSelect?: boolean
};

const ENTITY_CONFIG: Record<'user' | 'role' | 'day' | 'priority', EntityConfig> = {
    user: {
        dataKey: 'users',
        fallbackKey: 'user',
        allLabel: "Tous les utilisateurs",
        keyPrefix: "u_",
        valueKey: 'id'
    },
    role: {
        dataKey: 'roles',
        fallbackKey: 'role',
        allLabel: "Tous les rôles",
        keyPrefix: "r_",
        valueKey: 'id'
    },
    day: {
        dataKey: 'days',
        fallbackKey: 'day',
        allLabel: "Tous les jours",
        keyPrefix: "d_",
        valueKey: 'id'
    },
    priority: {
        dataKey: 'priorities',
        fallbackKey: 'priority',
        allLabel: "",
        keyPrefix: "p_",
        valueKey: 'id',
        multiSelect: false
    }
};

const ID_REF_FIELD_BY_KEY: Record<string, 'roleRefs' | 'roleToRefs' | 'roleMatchRefs' | 'userRefs' | 'userMatchRefs'> = {
    role: 'roleRefs',
    role_to: 'roleToRefs',
    role_ex: 'roleMatchRefs',
    role_with: 'roleMatchRefs',
    role_match: 'roleMatchRefs',
    user: 'userRefs',
    user_match: 'userMatchRefs'
};

function getBaseType(dataType: string): string {
    return dataType.split("_")[0] || dataType;
}

function isEntityType(baseType: string, dataType: string): 'user' | 'role' | 'day' | 'priority' | null {
    if (baseType === "user") {
        return "user";
    }

    if (baseType === "role") {
        return "role";
    }

    if (baseType === "day") {
        return "day";
    }

    if (baseType === "priority") {
        return "priority";
    }

    return null;
}

function findCatalogOption(catalog: any[], selectedValue: string): any {
    const matchById = catalog.find(entry => entry.id === selectedValue);
    if (matchById) {
        return matchById;
    }

    const lowerName = String(selectedValue).toLowerCase();
    const matchesByName = catalog.filter(entry => (entry.name || '').toLowerCase() === lowerName);
    return matchesByName.length === 1 ? matchesByName[0] : undefined;
}

function HelpArticleLink({ url }: Readonly<{ url?: string }>) {
    if (!url) {
        return null;
    }

    return (
        <Tooltip content="Voir l'article d'aide" placement="right">
            <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Ouvrir l'article d'aide dans un nouvel onglet"
                className="text-gray-500 hover:text-gray-700 ml-1 inline-flex"
            >
                <ExternalLink size={16} strokeWidth={2}/>
            </a>
        </Tooltip>
    );
}

const CONSTRAINTS_WITHOUT_WEEK: Set<string> = new Set([
     "simulRoles", "simulRolesGroups", "simulRolesWithExclusiveRoleGroups", "matchSimulRoles"
]);

const mapOfParamsToSkipInDisplay: Record<string, boolean> = {
    "note": true,
    "priority_type": true
}

// The grid is stored as a single JSON string in params.grid so it flows through the generic form plumbing.
function parseRoleUsersGrid(value: unknown): RoleUsersGrid {
    const raw = Array.isArray(value) ? value[0] : value;
    if (typeof raw !== "string" || raw === "") {
        return {};
    }
    let parsed: unknown;
    try {
        parsed = JSON.parse(raw);
    } catch {
        return {};
    }
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
        return {};
    }
    // A grid written outside this form must not break the rendering; the backend is stricter and ignores it whole.
    return Object.fromEntries(Object.entries(parsed)
        .filter(([, row]: [string, any]) => Array.isArray(row?.days) && row.days.length > 0)
        .map(([roleId, row]: [string, any]) => [roleId, {
            days: row.days.map(String),
            users: Array.isArray(row.users) && row.users.length > 0 ? row.users.map(String) : ["*"],
        }]));
}

function describeRoleUsersGrid(value: unknown, roles: Data["roles"]): string {
    const grid = parseRoleUsersGrid(value);
    const roleNameById = new Map((roles ?? []).map((r) => typeof r === "string" ? [r, r] : [r.id, r.name]));
    return Object.entries(grid)
        .map(([roleId, row]) => {
            const who = row.users.includes("*") ? "Tous" : `${row.users.length} utilisateur${row.users.length > 1 ? "s" : ""}`;
            return `${roleNameById.get(roleId) ?? roleId} (${who})`;
        })
        .join(", ");
}

function ConstraintBuilder(props: any) {
    let { handlers, info, operation } = props
    let { data, selectedConstraint, definitions }: {
        data: Data,
        selectedConstraint: Constraint,
        definitions: Definition[]
    } = info
    let { leftButton, rightButton } = handlers

    const [selection, setSelection] = useState<any[]>([])
    const [selected, setSelected] = useState<Constraint | string | null>(null)
    const [definition, setDefinition] = useState<Definition | null>(null)
    const [isNoteExpanded, setIsNoteExpanded] = useState(false)
    const [errors, setErrors] = useState<Record<number, string>>({});
    const [isActive, setIsActive] = useState(true);
    const [hideShiftAlerts, setHideShiftAlerts] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isDuplicating, setIsDuplicating] = useState(false);
    const [gridModalIndex, setGridModalIndex] = useState<number | null>(null);

    useEffect(() => {
        //might be unecessary now
        switch (operation) {
            case "edit": {
                let def = definitions?.find((e: any) => e.name == selectedConstraint.name)
                setDefinition(def)
                setSelected(selectedConstraint)
                let temp_selection = []
                for (let paramType of def?.params) {
                    const key = paramType[0];
                    const entityType = isEntityType(getBaseType(key), key);
                    const storedValue = selectedConstraint.params[key];
                    const usesIdSelection = entityType === 'role' || entityType === 'user';
                    const isWildcard = Array.isArray(storedValue) && storedValue.includes('*');

                    if (!usesIdSelection || isWildcard) {
                        temp_selection.push(storedValue);
                        continue;
                    }

                    const savedRefs = selectedConstraint[ID_REF_FIELD_BY_KEY[key]];
                    if (savedRefs && savedRefs.length > 0) {
                        temp_selection.push(savedRefs.map(ref => ref.id ?? ref.label));
                        continue;
                    }

                    const catalog: any[] = ((entityType === 'role' ? data.roles : data.users) as any[]) || [];
                    const storedNames: string[] = Array.isArray(storedValue) ? storedValue : (storedValue != null ? [storedValue] : []);
                    temp_selection.push(storedNames.map(name => findCatalogOption(catalog, name)?.id ?? name));
                }
                setSelection(temp_selection)
                setIsActive(selectedConstraint.active !== false)
                setHideShiftAlerts(selectedConstraint.hideShiftAlerts === true)
                break
            }
            case "create":
                setIsActive(true)
                setHideShiftAlerts(false)
                break
            case "display":
                setSelected(selectedConstraint)
                setIsActive(selectedConstraint.active !== false)
                setHideShiftAlerts(selectedConstraint.hideShiftAlerts === true)
                break
            default:
                break
        }
    }, [operation, data, selectedConstraint])

    function addEntry(selectedRule: string) {
        let def = definitions?.find((e: any) => e.name == selectedRule)
        setDefinition(def || null)
        setSelected(selectedRule)

        if (def?.params) {
            const prefilled = def.params.map((param: string[]) => {
                if (param[0] === "name") return [constraintDescriptions[def.name || selectedRule] || def.displayName || selectedRule];
                if (param[0] === "note") return ["-"];
                if (getBaseType(param[0]) === "day" && def.name === "simulRolesWithExclusiveRoleGroups") return ["*"];
                return undefined;
            });
            setSelection(prefilled);
        }
    }

    function onChangeType(e: any, index: any) {
        const value = e.target.value
        const names = value.split(",")
        setSelection((prev: any) => {
            const temp = [...prev]
            temp[index] = names
            return temp
        })
    }

    function selectType(value: any, index: any) {
        setSelection((prev: any) => {
            const temp = [...prev]
            temp[index] = Array.isArray(value) ? value : [value]
            return temp
        })
    }

    function onChangeNumber(e: any, index: any, min: number = 0, max?: number) {
        let value = e.target.value;
        if (value !== "") {
            const num = parseInt(value, 10);
            if (!isNaN(num)) {
                if (max !== undefined && num > max) value = String(max);
                else if (num < min) value = String(min);
            }
        }
        setSelection((prev: any) => {
            const temp = [...prev]
            temp[index] = [value]
            return temp
        })
    }

    function renderEntitySelector(entityType: 'user' | 'role' | 'day' | 'priority', dataType: string, index: number) {
        const config = ENTITY_CONFIG[entityType];
        const entityData = (data as any)[config.dataKey] || (data as any)[config.fallbackKey] || [];
        const label = capitalize((translation[dataType] || "").split(" ").slice(1).join(" "));
        const description = attributeDefinitions[dataType];
        const maxItems = (entityType === 'user' && definition?.name === 'simulRoles') ? undefined : undefined;

        return (
            <EntitySelector
                key={`${config.keyPrefix}${index}`}
                data={entityData}
                onSelect={selectType}
                index={index}
                value={selection[index]}
                label={label}
                description={description}
                allLabel={config.allLabel}
                valueKey={config.valueKey}
                maxItems={maxItems}
                multiSelect={config.multiSelect}
            />
        );
    }

    function renderTextInput(dataType: string, baseType: string, placeholder: any, index: number) {
        return (
            <DataText
                key={`t_${index}`}
                placeholder={selection[index] ?? placeholder}
                data={data[baseType as keyof typeof data]}
                onChange={(e: any, i: any) => {
                    onChangeType(e, i);
                    if (errors[i]) {
                        setErrors(prev => {
                            const newErrors = { ...prev };
                            delete newErrors[i];
                            return newErrors;
                        });
                    }
                }}
                index={index}
                type={dataType}
                error={errors[index]}
            />
        );
    }

    function renderNumberInput(dataType: string, baseType: string, placeholder: any, index: number, min?: number, max?: number) {
        const label = capitalize((translation[dataType] || "").split(" ").slice(1).join(" "));
        const description = attributeDefinitions[dataType];
        const rawValue = selection[index];
        const value = (rawValue && rawValue[0] !== undefined) ? rawValue[0] : (placeholder || "");

        return (
            <NumberInput
                key={`n_${index}`}
                label={label}
                description={description}
                value={value}
                min={min}
                max={max}
                onChange={(e) => {
                    onChangeNumber(e, index, min, max);
                    if (errors[index]) {
                        setErrors(prev => {
                            const newErrors = { ...prev };
                            delete newErrors[index];
                            return newErrors;
                        });
                    }
                }}
                error={errors[index]}
            />
        );
    }

    function renderSelectInput(dataType: string, baseType: string, placeholder: any, index: number) {
        let selectData = data[baseType as keyof typeof data];

        if (dataType === "time" && definition?.name && CONSTRAINTS_WITHOUT_WEEK.has(definition.name)) {
            let temp = (selectData || []).filter((option) => option !== "semaine") as typeof selectData;
            selectData = temp
        } else if (dataType !== "time")
            selectData = selectData?.filter((option: string) => option !== "semaine");

        return (
            <DataSelector
                key={`s_${index}`}
                placeholder={placeholder}
                data={selectData}
                onSelect={selectType}
                index={index}
                type={dataType}
            />
        );
    }

    function renderGridInput(dataType: string, index: number) {
        const label = capitalize((translation[dataType] || "").split(" ").slice(1).join(" "));
        const summary = describeRoleUsersGrid(selection[index], data.roles);

        return (
            <div key={`g_${index}`} className="flex flex-col gap-1 w-full">
                <div className="flex items-center gap-2">
                    <span className="text-base font-semibold">{label}</span>
                    <button
                        type="button"
                        aria-label="Modifier la grille de sélection"
                        onClick={() => setGridModalIndex(index)}
                        className="rounded p-1 text-amber-600 cursor-pointer hover:bg-gray-300"
                    >
                        <Pencil size={16}/>
                    </button>
                </div>
                <span className="text-sm text-gray-600">{summary || "Aucun rôle sélectionné"}</span>
                {errors[index] && <span className="text-red-600 text-xs mt-1">{errors[index]}</span>}
            </div>
        );
    }

    function renderParamInput(param: string[], index: number) {
        const [dataType, inputType] = param;

        if (!dataType || !inputType) {
            return null;
        }

        const baseType = getBaseType(dataType);
        const placeholder = selectedConstraint ? (selectedConstraint.params as any)[dataType] : "";

        const entityType = isEntityType(baseType, dataType);

        if (entityType) {
            //if (entityType === "day" && definition?.name === "simulRolesWithExclusiveRoleGroups") {
            //return null;
            //}
            return renderEntitySelector(entityType, dataType, index);
        }

        switch (inputType) {
            case "text":
                if (dataType === 'weight') {
                    return renderNumberInput(dataType, baseType, placeholder, index, 1, 10000);
                }
                if (['amount_shift', 'amount_time', 'time_before', 'time_after'].includes(dataType)) {
                    const max = (dataType === 'amount_shift' && definition?.name === 'spanBuffer') ? 1 : undefined;
                    return renderNumberInput(dataType, baseType, placeholder, index, 0, max);
                }
                return renderTextInput(dataType, baseType, placeholder, index);
            case "select":
                return renderSelectInput(dataType, baseType, placeholder, index);
            case "grid":
                return renderGridInput(dataType, index);
            case "none":
                return null;
            default:
                return null;
        }
    }


    function validateAndSubmit() {
        if (isSubmitting) {
            return;
        }

        const params = definition?.params;
        if (!params) {
            return;
        }

        const weightIndex = params.findIndex((e: any) => e[0] === "weight");
        if (weightIndex !== -1) {
            const val = Number(selection[weightIndex]);
            if (!Number.isInteger(val) || val < 1 || val > 10000) {
                setErrors({ [weightIndex]: "Le poids doit être un nombre entier entre 1 et 10000" });
                return;
            }
        }

        const gridIndex = params.findIndex((e: any) => e[1] === "grid");
        if (gridIndex !== -1 && Object.keys(parseRoleUsersGrid(selection[gridIndex])).length === 0) {
            setErrors({ [gridIndex]: "Sélectionnez au moins un jour pour un rôle" });
            return;
        }

        const positiveIntegerFields = ['amount_shift', 'amount_time', 'time_before', 'time_after'];
        for (const fieldName of positiveIntegerFields) {
            const fieldIndex = params.findIndex((e: any) => e[0] === fieldName);

            if (fieldIndex !== -1) {
                const val = Number(selection[fieldIndex]);
                if (fieldName === 'amount_shift' && definition?.name === 'spanBuffer') {
                    if (!Number.isInteger(val) || val < 0 || val > 1) {
                        setErrors({ [fieldIndex]: "La quantité doit être 0 ou 1" });
                        return;
                    }
                } else if (!Number.isInteger(val) || val < 0) {
                    setErrors({ [fieldIndex]: "La quantité doit être un nombre entier positif" });
                    return;
                }
            }

        }

        setIsSubmitting(true);
        Promise.resolve(leftButton(selection, selected, params, isActive, isSoftConstraint && hideShiftAlerts))
            .finally(() => setIsSubmitting(false));
    }

    // Hard and medium breaches must stay visible, so the alert toggle is offered on soft only.
    const priorityTypeIndex = definition?.params?.findIndex((param: string[]) => param[0] === "priority_type") ?? -1;
    const rawPriorityType = priorityTypeIndex >= 0 ? selection[priorityTypeIndex] : undefined;
    const formPriorityType = (Array.isArray(rawPriorityType) ? rawPriorityType[0] : rawPriorityType)
        || definition?.type
        || (selectedConstraint as Constraint)?.type;
    const isSoftConstraint = formPriorityType === "soft";

    let effPriorityTypeValue = "soft";
    let effDefaultPriorityTypeValue = effPriorityTypeValue;
    if (selectedConstraint) {
        effDefaultPriorityTypeValue = (selectedConstraint as Constraint).type;
        effPriorityTypeValue = (((selectedConstraint as Constraint).params as any)["priority_type"] || [])[0] || effDefaultPriorityTypeValue;
    }

    return (
        operation === "display" ?
            <div className={`bg-gray-100 w-full h-max flex flex-col gap-4 p-4 rounded-lg relative`}>
                {!isActive && (
                    <div className="absolute inset-0 bg-gray-200/50 pointer-events-none z-10 rounded-lg"/>
                )}
                <div className="flex items-start gap-5 justify-between w-full relative z-20">
                    <div className="flex items-center gap-3 w-2/3">
                        <div className="flex items-center font-semibold text-lg">
                            {typeof selectedConstraint == "string" ? selectedConstraint : constraintDescriptions[(selectedConstraint as Constraint)?.name] || selectedConstraint?.displayName}
                            <Tooltip
                                content={constraintDescriptions[(selectedConstraint as Constraint)?.name] || "Description non disponible"}
                                placement="right"
                                multiline
                                className="ml-2"
                            >
                                <button
                                    type="button"
                                    aria-label="Information sur la contrainte"
                                    className="text-gray-500 hover:text-gray-700 bg-transparent border-none p-0 cursor-help"
                                >
                                    <Info size={16}/>
                                </button>
                            </Tooltip>
                            <HelpArticleLink
                                url={definitions?.find((d: Definition) => d.name === (selectedConstraint as Constraint)?.name)?.helpUrl}/>
                        </div>
                    </div>
                    <span className="gap-2 items-center flex flex-shrink-0">
                        <Tooltip content={isActive ? "Désactiver" : "Activer"}>
                            <Switch
                                checked={isActive}
                                onChange={(val) => {
                                    setIsActive(val);
                                    handlers.onToggle && handlers.onToggle(selectedConstraint);
                                }}
                            />
                        </Tooltip>
                        {handlers.onDuplicate && selectedConstraint && (
                            <Tooltip content="Dupliquer">
                                <Button type="secondary" icon={<Copy size={20}/>}
                                        onClick={() => {
                                            setIsDuplicating(true);
                                            Promise.resolve(handlers.onDuplicate(selectedConstraint))
                                                .finally(() => setIsDuplicating(false));
                                        }}
                                        disabled={isDuplicating}/>
                            </Tooltip>
                        )}
                        <Tooltip content="Modifier">
                            <Button type="primary" icon={<Pencil size={20}/>}
                                    onClick={() => leftButton((selectedConstraint as Constraint).id)}/>
                        </Tooltip>
                        <Tooltip content="Effacer" placement="left">
                            <Button type="danger" icon={<Trash2 size={20}/>}
                                    onClick={() => rightButton((selectedConstraint as Constraint)?.id || null)}/>
                        </Tooltip>
                    </span>
                </div>

                {selectedConstraint ? (
                    <div className="flex flex-col gap-3 w-full">
                        {(selectedConstraint.params as any)['note'] && (
                            <div className="w-full max-w-5xl bg-white rounded-lg p-3">
                                <div className="text-sm">
                                    {(() => {
                                        const noteVal = (selectedConstraint.params as any)['note'];
                                        const noteText = Array.isArray(noteVal) ? noteVal.join(", ") : noteVal;
                                        const isLong = noteText.length > 500;

                                        if (isLong && !isNoteExpanded) {
                                            return (
                                                <>
                                                    {noteText.slice(0, 500)}...
                                                    <button
                                                        type="button"
                                                        className="text-blue-600 hover:underline cursor-pointer ml-1 bg-transparent border-none p-0 text-inherit"
                                                        onClick={() => setIsNoteExpanded(true)}
                                                    >
                                                        Lire plus
                                                    </button>
                                                </>
                                            )
                                        } else {
                                            return (
                                                <>
                                                    {noteText}
                                                    {isLong && (
                                                        <button
                                                            type="button"
                                                            className="text-blue-600 hover:underline cursor-pointer ml-1 bg-transparent border-none p-0 text-inherit"
                                                            onClick={() => setIsNoteExpanded(false)}
                                                        >
                                                            Lire moins
                                                        </button>
                                                    )}
                                                </>
                                            )
                                        }
                                    })()}
                                </div>
                            </div>
                        )}

                        <div className="flex flex-wrap items-start gap-3 w-full">
                            <Tooltip content={attributeDefinitions['type']} placement="right" multiline>
                                <div
                                    tabIndex={0}
                                    role="group"
                                    className="w-fit max-w-xs flex flex-col rounded-lg border border-gray-300 bg-white"
                                >
                                    <div className="px-3 py-1 bg-gray-600 text-white text-sm text-center rounded-t-lg">
                                        Type
                                    </div>
                                    <div className="px-3 py-2 text-center font-medium break-words rounded-b-lg">
                                        {translation[effPriorityTypeValue] + (effDefaultPriorityTypeValue !== effPriorityTypeValue ? " (*)" : "")}
                                    </div>
                                </div>
                            </Tooltip>

                            {Object.keys(selectedConstraint.params).filter(e => mapOfParamsToSkipInDisplay[e] !== true).map((e, idx) => {
                                const rawValue = (selectedConstraint.params as any)[e];
                                const value = e === "grid"
                                    ? describeRoleUsersGrid(rawValue, data.roles)
                                    : Array.isArray(rawValue) ? rawValue.join(", ") : rawValue;

                                return (
                                    <Tooltip key={idx} content={attributeDefinitions[e]} placement="bottom" multiline>
                                        <div
                                            tabIndex={0}
                                            role="group"
                                            className="w-fit max-w-xs flex flex-col rounded-lg border border-gray-300 bg-white"
                                        >
                                            <div
                                                className="px-3 py-1 bg-gray-600 text-white text-sm text-center rounded-t-lg">
                                                {capitalize((translation[e] || "").split(" ").slice(1).join(" "))}
                                            </div>
                                            <div className="px-3 py-2 text-center font-medium break-words rounded-b-lg">
                                                {value}
                                            </div>
                                        </div>
                                    </Tooltip>
                                )
                            })}
                        </div>
                    </div>
                ) : ""}
            </div>
            :
            <div className="bg-gray-200 w-full h-max flex flex-col gap-5 p-4 rounded-lg">
                <div>
                    {operation === "create" ? (
                        <div className="flex items-center gap-2 w-full relative justify-between">
                            <div className="flex-grow">
                                {(() => {
                                    const constraintOptions: SearchableOption[] = definitions?.toSorted((a: any, b: any) => (constraintDescriptions[a.name] || a.displayName || "").localeCompare(constraintDescriptions[b.name] || b.displayName || "", "fr")).map((e: any): SearchableOption => ({
                                        value: e.name,
                                        label: constraintDescriptions[e.name] || e.displayName || e.name,
                                        description: e.displayName,
                                    })) ?? [];
                                    const selectedName = typeof selected === "string" ? selected : selected?.name ?? null;
                                    return (
                                        <SearchableSelect
                                            options={constraintOptions}
                                            value={constraintOptions.find((o) => o.value === selectedName) ?? null}
                                            onChange={addEntry}
                                            placeholder="Choisissez une contrainte"
                                        />
                                    );
                                })()}
                            </div>

                            <div className="flex items-center gap-2">
                                {selected && (
                                    <Tooltip
                                        content={constraintDescriptions[typeof selected === 'string' ? selected : selected.name] || "Description non disponible"}
                                        placement="left"
                                        multiline
                                        className="shrink-0"
                                    >
                                        <button
                                            type="button"
                                            aria-label="Information sur la contrainte"
                                            className="text-gray-500 hover:text-gray-700 bg-transparent border-none p-0 cursor-help"
                                        >
                                            <Info size={16}/>
                                        </button>
                                    </Tooltip>
                                )}
                                <Tooltip content={isActive ? "Désactiver" : "Activer"} placement="left">
                                    <Switch checked={isActive} onChange={setIsActive}/>
                                </Tooltip>
                            </div>
                        </div>
                    ) : (
                        <div className="flex items-center gap-2 relative w-full justify-between">
                            <div className="flex items-center gap-2 font-semibold text-lg">
                                {typeof selectedConstraint == "string" ? selectedConstraint : constraintDescriptions[(selectedConstraint as Constraint)?.name] || selectedConstraint?.displayName}
                                <Tooltip
                                    content={constraintDescriptions[(selectedConstraint as Constraint)?.name] || "Description non disponible"}
                                    placement="right"
                                    multiline
                                >
                                    <button
                                        type="button"
                                        aria-label="Information sur la contrainte"
                                        className="text-gray-500 hover:text-gray-700 bg-transparent border-none p-0 cursor-help"
                                    >
                                        <Info size={16}/>
                                    </button>
                                </Tooltip>
                                <HelpArticleLink url={definition?.helpUrl}/>
                            </div>
                            <Tooltip content={isActive ? "Désactiver" : "Activer"} placement="left">
                                <Switch checked={isActive} onChange={setIsActive}/>
                            </Tooltip>
                        </div>
                    )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    {definition?.params.map((param: string[], index: number) =>
                        renderParamInput(param, index)
                    )}
                    <RoleUsersGridModal
                        open={gridModalIndex !== null}
                        onClose={() => setGridModalIndex(null)}
                        onConfirm={(grid) => {
                            if (gridModalIndex !== null) {
                                selectType([JSON.stringify(grid)], gridModalIndex);
                                setErrors(prev => {
                                    const newErrors = { ...prev };
                                    delete newErrors[gridModalIndex];
                                    return newErrors;
                                });
                            }
                            setGridModalIndex(null);
                        }}
                        roles={(data.roles ?? []).filter((r): r is Role => typeof r !== "string")}
                        users={data.users ?? []}
                        value={gridModalIndex !== null ? parseRoleUsersGrid(selection[gridModalIndex]) : {}}
                    />
                    {isSoftConstraint && (
                        <CheckboxField
                            label="Ne pas montrer d'alertes sur les quarts affectés par cette contrainte"
                            checked={hideShiftAlerts}
                            onChange={setHideShiftAlerts}
                        />
                    )}
                </div>

                <div className="flex justify-end gap-2">
                    <Tooltip content="Enregistrer les changements">
                        <Button type="success" icon={<Check size={20}/>}
                                onClick={validateAndSubmit} disabled={isSubmitting}/>
                    </Tooltip>
                    <Tooltip content="Annuler les changements" placement="left">
                        <Button type="danger" icon={<X size={20}/>}
                                onClick={() => rightButton((selectedConstraint as Constraint)?.id || null)}/>
                    </Tooltip>
                </div>
            </div>
    )
}

//TODO will have to pass or fetch departement ID
function App(props: any) {
    const { update, schedule } = props
    const [definitions, setDefinitions] = useState<Definition[]>([])
    const [data, setData] = useState<Data>({})
    const constraints = schedule?.constraints || [];
    const [currentConstraint, setCurrentConstraint] = useState({})

    const [depId, setDepId] = useState<string>()

    const [loading, setLoading] = useState(true)
    const [editId, setEditId] = useState<string | null>(null)
    const [show, setShow] = useState(false)
    const [isDeleting, setIsDeleting] = useState<string | null>(null)
    const [isToggling, setIsToggling] = useState<string | null>(null)
    const [isDuplicating, setIsDuplicating] = useState<string | null>(null)
    const [isAddingBase, setIsAddingBase] = useState(false)
    const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null)
    const [subTab, setSubTab] = useState<'constraints' | 'combinaisons'>('constraints')
    const [searchQuery, setSearchQuery] = useState("")

    const collapsedKeyParams = new URLSearchParams(globalThis.location.search);
    const COLLAPSED_KEY = `planifico:constraintCategoriesCollapsed:${collapsedKeyParams.get("bizId")}_${collapsedKeyParams.get("wgId")}`;

    const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(() => {
        try {
            const raw = localStorage.getItem(COLLAPSED_KEY);
            if (!raw) {
                return new Set();
            }

            const parsed: unknown = JSON.parse(raw);
            return Array.isArray(parsed)
                ? new Set(parsed.filter((k): k is string => typeof k === "string"))
                : new Set();
        } catch {
            return new Set();
        }
    })

    const [searchCollapsed, setSearchCollapsed] = useState<Set<string>>(new Set())

    useEffect(() => {
        let fetchDefs = async () => {
            let response = await fetch(BASE_URL + "constraints/def", {
                method: "GET"
            })
            let content = await response.json()
            setDefinitions(Array.isArray(content) ? content : [])
        }


        let fetchData = async () => {
            let response = await fetch(BASE_URL + "constraints/data", {
                method: "GET"
            })
            let content = await response.json()

            if (schedule?.employees) {
                const systemPlaceholders = new Set(["Bris de couverture", "Bris de service", "À combler", "A combler"]);

                content.users = schedule.employees
                    .filter((e: Employee | any) =>
                        e.name &&
                        typeof e.name === 'string' &&
                        e.name.length > 0 &&
                        !systemPlaceholders.has(e.name)
                    );

                const uniqueRoles = new Map<string, string>();
                schedule.shifts.forEach(shift => {
                    if (shift.roleId && shift.roleName) {
                        uniqueRoles.set(shift.roleId, shift.roleName);
                    }
                });
                schedule.employees.forEach(employee => {
                    if ((employee.roles?.length || 0) > 0) {
                        employee.roles?.forEach((role) => {
                            if (role.id && role.name) {
                                uniqueRoles.set(role.id, role.name);
                            }
                        })
                    }
                });

                let temp_roles = [...uniqueRoles].map(([id, name]) => ({ id, name }));
                const roleOrders = getRoleOrders(schedule);

                sortRoles(temp_roles, roleOrders, (r) => r.id, (r) => r.name);

                content.roles = temp_roles;
                content.days = DAYS_DATA;
                content.priorities = PRIORITIES_DATA;
            }

            setData(content)
        }

        let fetchAll = async () => {
            let params = new URLSearchParams(globalThis.location.search);
            let bizId = params.get("bizId");
            let wgId = params.get("wgId");
            let depId = ""

            if (bizId == null || wgId == null) {
                depId = "test_test"
            } else {
                depId = bizId + "_" + wgId
            }


            setDepId(depId)

            try {
                await fetchData()
                await fetchDefs()
                // await fetchConstraints(depId)
            } finally {
                setLoading(false)
            }
        }
        fetchAll()
    }, [schedule])

    useEffect(() => {
        try {
            localStorage.setItem(COLLAPSED_KEY, JSON.stringify([...collapsedCategories]));
        } catch {
        }
    }, [collapsedCategories, COLLAPSED_KEY])

    function expandCategories(keys: string[]) {
        setCollapsedCategories((prev) => {
            if (!keys.some((key) => prev.has(key))) {
                return prev;
            }

            const next = new Set(prev);
            keys.forEach((key) => next.delete(key));
            return next;
        });
    }

    function revealCategoryOf(constraintName: string) {
        expandCategories([categoryByName.get(constraintName) ?? "OTHER"]);
    }

    function toggleCategory(key: string) {
        const setter = query === "" ? setCollapsedCategories : setSearchCollapsed;

        setter((prev) => {
            const next = new Set(prev);

            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }

            return next;
        });
    }

    function handleSearchChange(value: string) {
        if (value.trim() === "") {
            setSearchCollapsed(new Set());
        }

        setSearchQuery(value);
    }

    function addConstraint(event) {
        setShow(true)
    }

    //split into submitnew and submitupdate
    function submit(constraintParams: string[], constraint: string | Constraint, paramTypes: any, active: boolean = true, hideShiftAlerts: boolean = false) {
        if (!depId) {
            return;
        }

        for (let input of constraintParams) {
            if (!input) {
                alert("Remplissez les champs avant de soumettre la constrainte")
                return
            }
        }
        if (typeof constraint == 'string') {
            constraint = definitions.find((e) => e.name == constraint)
        }
        if (!constraint || !paramTypes) {
            return
        }
        if (constraintParams.length != paramTypes.length) {
            alert("Remplissez les champs avant de soumettre la constrainte")
            return
        }


        //adding new param or multiple params to constraint
        let new_params = {}
        let i = 0
        for (let type of paramTypes) {
            if (constraintParams[i]) {
                new_params[type[0]] = constraintParams[i]
            } else {
                new_params[type[0]] = constraint.params[type[0]]
            }
            i++
        }

        let newConstraint: Constraint = {
            name: "",
            depId: depId,
            //HARD CODED FOR NOW
            multiplier: 1,
            active,
            hideShiftAlerts,
            params: new_params
        }


        if (typeof constraint == "string") {
            newConstraint.name = constraint
            newConstraint.displayName = definitions.find((e) => e.name == constraint)?.displayName || ""
            newConstraint.type = definitions.find((e) => e.name == constraint)?.type || "hard"
        } else {
            newConstraint.id = constraint.id
            newConstraint.name = constraint.name
            newConstraint.displayName = constraint.displayName
            let weight = constraintParams[paramTypes.findIndex((e: string[]) => e[0] === "weight")]

            newConstraint.weight = parseInt(weight) || 1
            newConstraint.type = definitions.find((e) => e.name == constraint.name)?.type || "hard"
            newConstraint.active = active
        }

        for (const paramType of paramTypes) {
            const key = paramType[0];
            const entityType = isEntityType(getBaseType(key), key);
            if (entityType !== 'role' && entityType !== 'user') {
                continue;
            }

            const catalog: any[] = ((entityType === 'role' ? data.roles : data.users) as any[]) || [];
            const selectedValue = new_params[key];
            const selectedIds: string[] = Array.isArray(selectedValue) ? selectedValue : (selectedValue != null ? [selectedValue] : []);

            const refField = ID_REF_FIELD_BY_KEY[key];
            const previousRefs: EntityRef[] =
                (typeof constraint !== 'string' && refField && constraint[refField]) || [];

            const labels: string[] = [];
            const refs: EntityRef[] = [];
            for (const selectedId of selectedIds) {
                if (selectedId === '*') {
                    labels.push('*');
                    continue;
                }

                const catalogOption = findCatalogOption(catalog, selectedId);
                if (catalogOption) {
                    labels.push(catalogOption.name);
                    refs.push({ id: catalogOption.id, label: catalogOption.name });
                    continue;
                }

                const previousRef = previousRefs.find(ref => ref.id === selectedId);
                if (previousRef?.label) {
                    labels.push(previousRef.label);
                    refs.push({ id: previousRef.id, label: previousRef.label });
                } else {
                    labels.push(selectedId);
                    refs.push({ label: selectedId });
                }
            }

            new_params[key] = labels;
            if (refField) {
                newConstraint[refField] = refs;
            }
        }

        return fetch(`${BASE_URL}constraints`, {
            headers: {
                "Content-Type": "application/json",  // tell server you're sending JSON
            },
            method: "POST",
            body: JSON.stringify(newConstraint)
        }).then(() => {
            update()
            if (show) {
                setSearchQuery("")
            }
            setShow(false)
            setEditId("")
            revealCategoryOf(newConstraint.name)
        })
    }

    function edit(id: string) {
        setCurrentConstraint(constraints[id])
        setEditId(id)
        update()
        return "test"
    }

    async function deleteConstraint(id: string) {
        if (isDeleting) {
            return;
        }

        setIsDeleting(id);

        try {
            await fetch(BASE_URL + "constraints", {
                headers: {
                    "Content-Type": "application/json",  // tell server you're sending JSON
                },
                method: "DELETE",
                body: id
            })
            update()
        } finally {
            setIsDeleting(null);
        }
    }

    // "X" -> "X (copie)" -> "X (copie 2)" -> ..., numbered from the copies already saved.
    function nextCopyName(originalName: string): string {
        const base = originalName.replace(/\s*\(copie(?:\s+\d+)?\)\s*$/i, "").trim();
        const pattern = new RegExp(`^${base.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\s*\\(copie(?:\\s+(\\d+))?\\)$`, "i");

        let highest = 0;
        constraints.forEach((c: Constraint) => {
            const rawName = (c.params as any)?.name;
            const name = Array.isArray(rawName) ? rawName[0] : rawName;
            const match = typeof name === "string" ? pattern.exec(name.trim()) : null;
            if (match) {
                highest = Math.max(highest, match[1] ? Number(match[1]) : 1);
            }
        });

        return highest === 0 ? `${base} (copie)` : `${base} (copie ${highest + 1})`;
    }

    async function duplicateConstraint(constraint: Constraint) {
        if (isDuplicating || !constraint) {
            return;
        }

        setIsDuplicating(constraint.id || null);

        try {
            const { id, ...rest } = constraint;
            const rawName = (constraint.params as any)?.name;
            const currentName = Array.isArray(rawName) ? rawName[0] : rawName;
            const params = { ...constraint.params } as any;

            if (typeof currentName === "string") {
                const copyName = nextCopyName(currentName);
                params.name = Array.isArray(rawName) ? [copyName] : copyName;
            }

            const response = await fetch(`${BASE_URL}constraints`, {
                headers: {
                    "Content-Type": "application/json",
                },
                method: "POST",
                body: JSON.stringify({ ...rest, params })
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            setEditId("");
            update();
            revealCategoryOf(constraint.name);
        } catch (e) {
            alert("Erreur lors de la duplication de la contrainte");
        } finally {
            setIsDuplicating(null);
        }
    }

    function requestDeleteConstraint(id: string | null) {
        if (!id) return;
        setPendingDeleteId(id);
    }

    async function toggleActive(constraint: Constraint) {
        if (isToggling) {
            return;
        }

        setIsToggling(constraint.id || null);

        try {
            const newHelper = {
                ...constraint,
                active: !constraint.active
            };

            await fetch(`${BASE_URL}constraints`, {
                headers: {
                    "Content-Type": "application/json",
                },
                method: "POST",
                body: JSON.stringify(newHelper)
            });

            update();
        } finally {
            setIsToggling(null);
        }
    }

    function cancel() {
        setShow(false)
        setEditId(null)
        return "test"
    }

    async function addBaseRules() {
        if (isAddingBase || !depId) return;
        setIsAddingBase(true);

        const baseConstraints = [
            {
                name: "mandatory",
                displayName: "Minimum de quarts doivent être assignés (Quarts obligatoires)",
                type: "hard",
                params: {
                    role: ["*"],
                    weight: ["100"],
                    name: ["1. BASE - Assigner les bris de service (uniquement)"],
                    note: ["-"]
                }
            },
            {
                name: "requiredSkills",
                displayName: "Compétence requise pour être assigné au quart",
                type: "hard",
                params: {
                    weight: ["100"],
                    name: ["1. BASE - La personne assignée doit être compétente pour ce rôle"],
                    note: ["-"]
                }
            },
            {
                name: "minUnassigned",
                displayName: "Minimiser les bris de service",
                type: "soft",
                params: {
                    weight: ["100"],
                    name: ["1. BASE - Assigner les bris de services et quarts à combler"],
                    note: ["-"]
                }
            },
            {
                name: "unavailable",
                displayName: "Absence",
                type: "hard",
                params: {
                    weight: ["100"],
                    name: ["1. BASE - Ne pas assigner les personnes lors de leurs congés"],
                    note: ["-"]
                }
            },
            {
                name: "undesired",
                displayName: "Indisponibilité",
                type: "hard",
                params: {
                    weight: ["100"],
                    name: ["1. BASE - Ne pas assigner les personnes lors de leurs indisponibles"],
                    note: ["-"]
                }
            },
            {
                name: "desired",
                displayName: "Date souhaitée",
                type: "soft",
                params: {
                    weight: ["100"],
                    name: ["1. BASE - Tenter d'assigner les personnes lors de leurs préférence de disponibilités"],
                    note: ["-"]
                }
            },
            {
                name: "unassigned",
                displayName: "Bris de service par rôle",
                type: "soft",
                params: {
                    role: ["*"],
                    day: ["*"],
                    weight: ["100"],
                    name: ["1. BASE - Assigner les quarts à combler pour certains rôles"],
                    note: ["-"]
                }
            },
            {
                name: "equityEmployeeRole",
                displayName: "Équité entre les employés pour un même rôle",
                type: "soft",
                params: {
                    weight: ["100"],
                    name: ["3. ÉQUITÉ ET BALANCEMENT - Balancement des quarts selon l'objectif de ratio en pourcentage de la table d'équité"],
                    note: ["-"]
                }
            },
        ];

        try {
            await Promise.all(
                baseConstraints.map(c =>
                    fetch(`${BASE_URL}constraints`, {
                        headers: { "Content-Type": "application/json" },
                        method: "POST",
                        body: JSON.stringify({
                            ...c,
                            depId,
                            multiplier: 1,
                            active: true,
                            weight: 100,
                        })
                    }).then(r => {
                        if (!r.ok) throw new Error(`HTTP ${r.status}`);
                        return r;
                    })
                )
            );
            update();
            baseConstraints.forEach((c) => revealCategoryOf(c.name));
        } catch (e) {
            alert("Erreur lors de la création des règles de base");
        } finally {
            setIsAddingBase(false);
        }
    }

    const constraintSearchText = (c: Constraint) => {
        const title = constraintDescriptions[c.name] || c.displayName || c.name || "";
        const rawNote = (c.params as any)?.note;
        const note = Array.isArray(rawNote) ? rawNote.join(" ") : (rawNote ?? "");
        return normalize(`${title} ${note}`);
    };

    const query = normalize(searchQuery.trim());
    const visibleConstraints = constraints
        .filter((c: Constraint) => c.id === editId || constraintSearchText(c).includes(query))
        .sort((a: Constraint, b: Constraint) =>
            constraintSearchText(a).localeCompare(constraintSearchText(b), "fr")
        );

    const categoryByName = useMemo(() => {
        const map = new Map<string, string>();

        definitions.forEach((def) => {
            if (def.category) {
                map.set(def.name, def.category);
            }
        });

        return map;
    }, [definitions]);

    const hasCategories = categoryByName.size > 0;

    const categoryOf = (c: Constraint): string => categoryByName.get(c.name) ?? "OTHER";

    const groupedConstraints = new Map<string, Constraint[]>();

    if (hasCategories) {
        visibleConstraints.forEach((c: Constraint) => {
            const key = categoryOf(c);
            const group = groupedConstraints.get(key);

            if (group) {
                group.push(c);
            } else {
                groupedConstraints.set(key, [c]);
            }
        });
    }

    const orderedGroupKeys = [
        ...CATEGORY_ORDER.filter((key) => groupedConstraints.has(key)),
        ...[...groupedConstraints.keys()]
            .filter((key) => !KNOWN_CATEGORIES.has(key))
            .sort((a, b) => a.localeCompare(b, "fr")),
    ];

    const editingConstraint = editId ? constraints.find((c: Constraint) => c.id === editId) : null;
    const editingCategoryKey = editingConstraint ? categoryOf(editingConstraint) : null;

    const isSectionOpen = (key: string) =>
        key === editingCategoryKey || !(query === "" ? collapsedCategories : searchCollapsed).has(key);

    const renderConstraintCard = (e: Constraint, i: number) => (
        e.id === editId ? (
            <ConstraintBuilder
                key={e.id || i}
                handlers={{ leftButton: submit, rightButton: cancel }}
                info={{ definitions, data, selectedConstraint: e }}
                operation={"edit"}/>
        ) : (
            <ConstraintBuilder
                key={e.id || i}
                handlers={{
                    leftButton: edit,
                    rightButton: requestDeleteConstraint,
                    onToggle: toggleActive,
                    onDuplicate: duplicateConstraint
                }}
                info={{ definitions, data, selectedConstraint: e }}
                operation={"display"}/>
        )
    );

    return (
        <div className="mx-auto py-2 relative z-10">
            <div className="my-2">
                <SegmentedTabs
                    options={[
                        { value: 'constraints', label: 'Contraintes' },
                        { value: 'combinaisons', label: 'Combinaisons de tâches' },
                    ]}
                    value={subTab}
                    onChange={setSubTab}
                />
            </div>
            {subTab === 'combinaisons' ? (
                <RoleCombinations roles={(data.roles ?? []).map((r: any) => ({ id: r.id, name: r.name }))}/>
            ) : (
                <>
                    <div className="my-4 flex items-center gap-2">
                        {!loading && <Button label="Ajouter Contrainte" onClick={() => addConstraint(null)}/>}
                        {!loading && constraints.length === 0 && !isAddingBase && (
                            <Button label="Ajouter règles de base" onClick={addBaseRules}/>
                        )}
                        {!loading && constraints.length > 0 && (
                            <SearchInput
                                className="ml-auto w-full max-w-xs"
                                value={searchQuery}
                                onChange={(e) => handleSearchChange(e.target.value)}
                                placeholder="Rechercher une contrainte…"
                                aria-label="Rechercher une contrainte"
                            />
                        )}
                    </div>
                    <div className="flex flex-col gap-4 mb-2 w-full">
                        {
                            show && data && definitions ?
                                <ConstraintBuilder handlers={{ leftButton: submit, rightButton: cancel }}
                                                   info={{ definitions: definitions, data: data }}
                                                   operation={"create"}/> :
                                ""
                        }
                        {
                            loading ? "" :
                            hasCategories ?
                                orderedGroupKeys.map((key) => {
                                    const group = groupedConstraints.get(key) ?? [];

                                    return (
                                        <CollapsibleSection
                                            key={key}
                                            title={categoryLabel(key)}
                                            count={group.length}
                                            open={isSectionOpen(key)}
                                            onToggle={() => toggleCategory(key)}
                                            contentClassName="flex flex-wrap gap-4 w-full"
                                        >
                                            {group.map(renderConstraintCard)}
                                        </CollapsibleSection>
                                    );
                                })
                                :
                                <div className="flex flex-wrap gap-4 w-full">
                                    {visibleConstraints.map(renderConstraintCard)}
                                </div>
                        }
                        {!loading && query !== "" && visibleConstraints.length === 0 && (
                            <p className="text-muted-foreground text-sm w-full py-4">
                                Aucune contrainte ne correspond à « {searchQuery} ».
                            </p>
                        )}
                    </div>
                    {(() => {
                        const pending = pendingDeleteId ? constraints.find((c: Constraint) => c.id === pendingDeleteId) : null;
                        const label =
                            (pending && constraintDescriptions[pending.name]) ||
                            pending?.displayName ||
                            "cette contrainte";
                        return (
                            <ConfirmModal
                                open={pendingDeleteId !== null}
                                onClose={() => setPendingDeleteId(null)}
                                onConfirm={() => {
                                    if (pendingDeleteId) deleteConstraint(pendingDeleteId);
                                }}
                                title="Supprimer la contrainte"
                                message={`Voulez-vous vraiment effacer « ${label} » ?`}
                                confirmLabel="Supprimer"
                                cancelLabel="Annuler"
                                confirmVariant="danger"
                                confirmDisabled={isDeleting === pendingDeleteId}
                            />
                        );
                    })()}
                </>
            )}
        </div>
    );
}

export default App;
